import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { unwrapList } from '@/lib/guest';
import {
  menuLocale,
  readCachedMenu,
  sanitizeSections,
  writeCachedMenu,
  type GuestMenuSection,
} from '@/lib/menuCache';
import { useAppSelector } from '@/Redux/store';
import { getTenantId } from '@/lib/api';

export type { GuestMenuItem, GuestMenuSection } from '@/lib/menuCache';

export interface GuestMenuState {
  sections: GuestMenuSection[];
  /** `loading` only until *something* is on screen — cache counts. */
  status: 'loading' | 'ready' | 'error';
  source: 'cache' | 'network' | null;
  /** Epoch ms of the cached copy, when that is what is showing. */
  cachedAt: number | null;
  /** Cached content is on screen and the network has not confirmed it. */
  stale: boolean;
  revalidating: boolean;
  /** Force a network read — the order path calls this after a 422. */
  refresh: () => Promise<GuestMenuSection[] | null>;
}

/**
 * Stale-while-revalidate over `GET /menus/guest`.
 *
 * The cached copy is painted the moment it is read and the network answer
 * replaces it when it lands; the guest never watches a spinner for a menu this
 * device has already seen. Both reads start together rather than in sequence,
 * so a warm network is not slowed down by the IndexedDB round trip — whichever
 * arrives first wins, and a late cache read can never overwrite fresh data.
 *
 * Keyed by branch AND locale: the API localizes from `x-lang`, so an Arabic
 * guest must never be handed the English copy. Changing the language changes
 * the key, which is why switching locales re-reads rather than reusing.
 */
export function useGuestMenu(): GuestMenuState {
  const { i18n } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const branchId = session?.branchId ?? null;
  const tenantId = session?.tenantId ?? getTenantId();
  const locale = menuLocale(i18n.language);

  const [state, setState] = useState<Omit<GuestMenuState, 'refresh'>>({
    sections: [],
    status: 'loading',
    source: null,
    cachedAt: null,
    stale: false,
    revalidating: false,
  });

  /**
   * Bumped on every branch/locale change and on every forced refresh. A reply
   * from an older key is dropped rather than painted — this is what stops an
   * in-flight English fetch from landing after the guest switched to Arabic.
   */
  const generation = useRef(0);

  const fetchNetwork = useCallback(
    async (gen: number, hadContent: boolean): Promise<GuestMenuSection[] | null> => {
      if (!branchId) return null;
      try {
        const { data } = await api.get('/menus/guest', { params: { branchId } });
        const sections = sanitizeSections(unwrapList<unknown>(data));
        if (generation.current !== gen) return sections;
        setState({
          sections,
          status: 'ready',
          source: 'network',
          cachedAt: null,
          stale: false,
          revalidating: false,
        });
        // Written after painting, never before: persistence must not delay a frame.
        void writeCachedMenu(tenantId, branchId, locale, sections);
        return sections;
      } catch {
        if (generation.current !== gen) return null;
        // Offline, or the API is unreachable on this venue's Wi-Fi. Cache on
        // screen stays and is now openly labelled stale; nothing on screen at
        // all is the only case that becomes an error.
        setState((prev) => ({
          ...prev,
          status: hadContent || prev.sections.length ? 'ready' : 'error',
          stale: prev.source === 'cache' || prev.sections.length > 0,
          revalidating: false,
        }));
        return null;
      }
    },
    [branchId, tenantId, locale],
  );

  useEffect(() => {
    const gen = ++generation.current;
    if (!branchId) {
      setState({
        sections: [],
        status: 'ready',
        source: null,
        cachedAt: null,
        stale: false,
        revalidating: false,
      });
      return;
    }

    setState({
      sections: [],
      status: 'loading',
      source: null,
      cachedAt: null,
      stale: false,
      revalidating: true,
    });

    // Deliberately not awaited before the network call below: the two race and
    // the fresher one wins.
    void readCachedMenu(tenantId, branchId, locale).then((entry) => {
      if (!entry || generation.current !== gen) return;
      setState((prev) =>
        // The network already answered — a cache read must not walk it back.
        prev.source === 'network'
          ? prev
          : {
              sections: entry.sections,
              status: 'ready',
              source: 'cache',
              cachedAt: entry.cachedAt,
              stale: false,
              revalidating: true,
            },
      );
    });

    void fetchNetwork(gen, false);
  }, [branchId, tenantId, locale, fetchNetwork]);

  const refresh = useCallback(() => {
    const gen = ++generation.current;
    setState((prev) => ({ ...prev, revalidating: true }));
    return fetchNetwork(gen, true);
  }, [fetchNetwork]);

  return { ...state, refresh };
}
